import { Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material'
import PaymentIcon from '@mui/icons-material/Payment'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import { EarnestMoneyDialog } from '../../dialogs/EarnestMoneyDialog'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useEarnestMoneyFlow } from '../../../../logic/useEarnestMoneyFlow'
import { DownloadUploadsComponent } from '../../../DownloadUploadsComponent'
import { useContent, useUserResetTask } from '../../../../logic'
import { toTitleCase } from '../../../../utils'
import { Alert } from '@mui/material'
import { useGetNotification, useGetOrder } from '../../../../logic/useApiQuery'
import { apiRoutes } from '../../../../logic/apiRoutes'
import { PortalNotification } from '../../../../types/portalTypes/PortalNotification'
import { EarnestMoneyContactDialog, ContactInfo } from './EarnestMoneyContactDialog'
import { useUserCompleteTask } from '../../../../logic/useApiMutation'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingScreen } from '../../LoadingScreen'
import { useOptimisticUpdateNotificationAndOrderNotification } from '../../../../logic/optimisticUpdates'
import { LoadingButton } from '@mui/lab'
import { ResetTaskDialog } from '../../dialogs/ResetTaskDialog'

export function EarnestMoneyTask({ orderKey, notificationId }: {
  orderKey: string, notificationId: string,
}) {
  const { search } = useLocation()
  const { data: order } = useGetOrder(orderKey, search)
  const { data: notification } = useGetNotification()
  const queryClient = useQueryClient()
  const [userResetOpen, setUserResetOpen] = useState(false)

  const isCommercial = notification?.Configuration?.IsCommercial ?? false
  const userMappingInfo = order?.userMappingInfo
  const needsContactInfo = isCommercial ? !userMappingInfo?.entityName : !(userMappingInfo?.firstName && userMappingInfo?.lastName)

  const [showEarnestMoneyDialog, setShowEarnestMoneyDialog] = useState(false)
  const [showContactDialog, setShowContactDialog] = useState(false)
  const [showPaidAnotherWayConfirm, setShowPaidAnotherWayConfirm] = useState(false)
  const initialContactInfo: ContactInfo | undefined = !needsContactInfo
    ? isCommercial && userMappingInfo?.entityName
      ? { entityName: userMappingInfo.entityName, isCommercial: true }
      : !isCommercial && userMappingInfo?.firstName && userMappingInfo?.lastName
        ? { firstName: userMappingInfo.firstName, lastName: userMappingInfo.lastName, isCommercial: false }
        : undefined
    : undefined
  const [contactInfo, setContactInfo] = useState<ContactInfo | undefined>(initialContactInfo)

  const earnestMoneyDialogClicked = async () => {
    if (needsContactInfo && !contactInfo) {
      setShowContactDialog(true)
    } else {
      setShowEarnestMoneyDialog(true)
    }
  }

  const handleContactSubmit = (info: ContactInfo) => {
    setContactInfo(info)
    localStorage.removeItem('$activeInteraction')
    localStorage.removeItem('$k')
    initiatePaymentMutation.mutate({
      isCommercial: info.isCommercial,
      ...(info.isCommercial === false && { firstName: info.firstName, lastName: info.lastName }),
      ...(info.isCommercial === true && { entityName: info.entityName }),
    }, {
      onSuccess: () => {
        setShowContactDialog(false)
        setShowEarnestMoneyDialog(true)
      }
    });
  }

  const { TaskText } = useContent()
  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()

  const {
    isNotificationStatusesQueryLoading,
    initiatePaymentMutation,
    cachedInitiatePaymentData,
    isPaymentSubmitted,
    isPaymentAcknowledged,
    isPaymentCompleted,
  } = useEarnestMoneyFlow(orderKey, notificationId, search, contactInfo, needsContactInfo)

  const paidAnotherWayMutation = useUserCompleteTask(orderKey, notificationId, search, {
    onMutate: () => {
      if (notification?.NotificationId) {
        optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notification.NotificationId, search, patch: { IsComplete: true } })
        queryClient.setQueryData(
          [apiRoutes.getNotificationStatusesV2(orderKey, notificationId, search)],
          (old: Partial<PortalNotification> | undefined) => ({ ...old, IsComplete: true })
        )
      }
      setShowPaidAnotherWayConfirm(false)
    }
  })

  const { mutate: mutateUserResetTask, isLoading: userResetLoading } = useUserResetTask(orderKey, notification?.NotificationId || '', search, {
    onMutate: () => {
      if (notification?.NotificationId) {
        optimisticUpdateNotificationAndOrderNotification({
          orderKey, notificationId: notification.NotificationId, search, patch:
            { IsFormComplete: false, IsSigningCancelled: false, IsSigningComplete: false, IsCancelled: false, IsComplete: false }
        })
        // Reset cached statuses so the flow restarts
        queryClient.setQueryData(
          [apiRoutes.getNotificationStatusesV2(orderKey, notificationId, search)],
          (old: Partial<PortalNotification> | undefined) => ({
            ...old,
            IsComplete: false,
            IsPaymentSubmitted: false,
            IsPaymentAcknowledged: false,
            IsFormComplete: false,
            IsCancelled: false,
          })
        )
        queryClient.removeQueries([apiRoutes.initiatePayment(orderKey, notificationId, search)])
        initiatePaymentMutation.reset()
        localStorage.removeItem('$activeInteraction')
        localStorage.removeItem('$k')
        setContactInfo(initialContactInfo)
        setUserResetOpen(false)

        // Re-initiate payment if contact info is available
        if (!needsContactInfo && initialContactInfo) {
          initiatePaymentMutation.mutate({
            isCommercial: initialContactInfo.isCommercial,
            ...(initialContactInfo.isCommercial === false && { firstName: initialContactInfo.firstName, lastName: initialContactInfo.lastName }),
            ...(initialContactInfo.isCommercial === true && { entityName: initialContactInfo.entityName }),
          })
        }
      }
    }
  })

  const initiatePaymentData = cachedInitiatePaymentData || initiatePaymentMutation?.data

  const userSubmittedPayment = isPaymentSubmitted || isPaymentAcknowledged
  
  const showEarnestMoneyIsComplete = isPaymentCompleted

  const showRestartButton = showEarnestMoneyIsComplete && !userSubmittedPayment

  const showDownloadUploadsButton = isPaymentSubmitted || isPaymentAcknowledged

  if (isNotificationStatusesQueryLoading && !notification?.IsCancelled) {
    return (
      <LoadingScreen />
    )
  }

  if (notification?.IsCancelled) { //The portalTaskComponent handles the cancelled state, so we don't need to render anything here
    return <></>
  }

  return (
    <>
      {showEarnestMoneyIsComplete ? (
        <>
          <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
            Thank you for completing your {TaskText}!
          </Alert>
          {showRestartButton && (
            <LoadingButton
              loading={userResetLoading}
              variant="text"
              sx={{
                borderRadius: "40px",
                minWidth: "250px",
                color: "primary.main",
                mt: 1,
              }}
              onClick={() => setUserResetOpen(true)}
            >
              Restart this {TaskText}
            </LoadingButton>
          )}
        </>
      ) : isPaymentAcknowledged || isPaymentSubmitted ? (
        <Box sx={{ textAlign: 'left', alignItems: 'flex-start', width: '100%' }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Your earnest money payment has been initiated, and your receipt is available below. Once the title company approves and processes your payment, the funds will be debited from your account.
          </Alert>
        </Box>
      ) : !isPaymentSubmitted && !initiatePaymentMutation?.error ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, py: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={earnestMoneyDialogClicked}
            startIcon={!initiatePaymentData && (!needsContactInfo || !!contactInfo) ? <CircularProgress size={20} /> : <PaymentIcon />}
            disabled={!initiatePaymentData && (!needsContactInfo || !!contactInfo)}
            sx={{ borderRadius: '40px', minWidth: 260, py: 1.2 }}
          >
            {!initiatePaymentData && (!needsContactInfo || !!contactInfo) ? 'Loading...' : 'Complete Earnest Money'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => setShowPaidAnotherWayConfirm(true)}
            startIcon={<SyncAltIcon />}
            sx={{ borderRadius: '40px', minWidth: 260, py: 1.2 }}
          >
            I Paid Another Way
          </Button>
        </Box>
      ) : initiatePaymentMutation?.error ? (
        <Box sx={{ textAlign: 'left', alignItems: 'flex-start', width: '100%' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {initiatePaymentMutation.error.message}
          </Alert>
        </Box>
      ) : null}

      {showDownloadUploadsButton && (
        <DownloadUploadsComponent
          button={
            <Button
              variant="contained"
              sx={{
                borderRadius: "40px",
                minWidth: "250px",
                color: "white",
              }}
            >
              {`Download ${toTitleCase(TaskText)} Documents`}
            </Button>
          }
        />
      )}
      {
        showEarnestMoneyDialog &&
        initiatePaymentData &&
        <EarnestMoneyDialog
          regCode={initiatePaymentData.Registration}
          interactionPrefix={initiatePaymentData.InitiateEMDResponse.interactionPrefix}
          interactionSuffix={initiatePaymentData.InitiateEMDResponse.interactionSuffix}
          onClosed={() => { setShowEarnestMoneyDialog(false) }}
        />
      }
      <EarnestMoneyContactDialog
        open={showContactDialog}
        loading={initiatePaymentMutation.isLoading}
        onSubmit={handleContactSubmit}
        onClose={() => setShowContactDialog(false)}
      />
      <Dialog open={showPaidAnotherWayConfirm} onClose={() => setShowPaidAnotherWayConfirm(false)}>
        <DialogTitle>I Paid Another Way</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By confirming, you acknowledge that your earnest money deposit was submitted outside of this portal. This task will be marked as complete and no further action will be needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaidAnotherWayConfirm(false)} disabled={paidAnotherWayMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => paidAnotherWayMutation.mutate(undefined, {
              onSuccess: () => setShowPaidAnotherWayConfirm(false),
            })}
            disabled={paidAnotherWayMutation.isLoading}
          >
            {paidAnotherWayMutation.isLoading ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      <ResetTaskDialog
        open={userResetOpen}
        setUserResetOpen={setUserResetOpen}
        onUserResetTask={() => { mutateUserResetTask(undefined)}}
      />
    </>
  )
}
